import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { format, parseISO, isValid } from 'date-fns';
import { CalendarIcon, ClockIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { supabase } from '../lib/supabase';
import type { Event, DayEvents } from '../types/content';

function ScheduleSection() {
  const [schedule, setSchedule] = useState<DayEvents[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTournamentAndEvents();
  }, []);

  const fetchTournamentAndEvents = async () => {
    try {
      const { data: tournaments, error: tournamentError } = await supabase
        .from('tournaments')
        .select('id, name')
        .eq('status', 'upcoming')
        .order('start_date', { ascending: true })
        .limit(1);

      if (tournamentError) {
        console.error("Error fetching tournaments:", tournamentError);
        setDefaultEvents();
        setLoading(false);
        return;
      }

      const upcomingTournament = tournaments && tournaments.length > 0 ? tournaments[0] : null;

      if (upcomingTournament?.id) {
        await fetchEvents(upcomingTournament.id);
      } else {
        setDefaultEvents();
        setLoading(false);
      }
    } catch (err) {
      console.error('Error fetching tournament:', err);
      setDefaultEvents();
      setLoading(false);
    }
  };

  const fetchEvents = async (id: string) => {
    try {
      setLoading(true);
      
      // Check if ID is valid UUID
      if (!isValidUUID(id)) {
        console.warn('Invalid tournament ID format, using default events');
        setDefaultEvents();
        return;
      }
      
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('tournament_id', id)
        .order('date', { ascending: true });
      
      if (error) {
        console.error("Error fetching events:", error);
        setDefaultEvents();
        return;
      }
      
      if (data && data.length > 0) {
        // Group events by date
        const eventsByDate = data.reduce((acc: Record<string, Event[]>, event) => {
          // Validate date format
          if (!event.date || !isValid(new Date(event.date))) {
            console.warn(`Invalid date format for event: ${event.id}`);
            return acc;
          }
          
          const date = event.date;
          if (!acc[date]) {
            acc[date] = [];
          }
          
          acc[date].push({
            id: event.id,
            date: event.date,
            time: event.time,
            title: event.title,
            location: event.location,
            description: event.description || ''
          });
          
          return acc;
        }, {});
        
        // Convert to array format needed by the component
        const formattedEvents: DayEvents[] = Object.entries(eventsByDate).map(([date, dayEvents], index) => ({
          id: index + 1,
          date,
          events: dayEvents.sort((a, b) => a.time.localeCompare(b.time))
        }));
        
        // Only show the first two days for simplicity
        setSchedule(formattedEvents.slice(0, 2));
      } else {
        // Use default events if no data
        setDefaultEvents();
      }
    } catch (err) {
      console.error('Error fetching events:', err);
      setDefaultEvents();
    } finally {
      setLoading(false);
    }
  };

  // Helper function to validate UUID format
  const isValidUUID = (uuid: string) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuid && uuidRegex.test(uuid);
  };

  const setDefaultEvents = () => {
    setSchedule([]);
  };

  if (!loading && schedule.length === 0) return null;

  return (
    <section className="py-24 bg-golf-sand relative overflow-hidden">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-7xl mx-auto"
        >
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-golf-gray mb-6"
            >
              Tournament Schedule
            </motion.h2>
            <p className="text-xl text-gray-600">
              Key events for the Ibom Open 2025
            </p>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
              {[1, 2].map((i) => (
                <div key={i} className="bg-white rounded-3xl shadow-lg overflow-hidden animate-pulse">
                  <div className="bg-gray-300 p-6 h-20"></div>
                  <div className="p-6 space-y-6">
                    <div className="h-16 bg-gray-200 rounded"></div>
                    <div className="h-16 bg-gray-200 rounded"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : schedule.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
              {schedule.map((day, dayIndex) => (
                <motion.div
                  key={dayIndex}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: dayIndex * 0.2 }}
                  className="bg-white rounded-3xl shadow-lg overflow-hidden"
                >
                  <div className="bg-golf-gray p-4 sm:p-6">
                    <div className="flex items-center space-x-3">
                      <CalendarIcon className="w-6 h-6 text-golf-orange" />
                      <h3 className="text-xl sm:text-2xl font-display font-bold text-white">
                        {format(parseISO(day.date), 'MMMM d, yyyy')}
                      </h3>
                    </div>
                  </div>

                  <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                    {/* Only show the first 2 events per day for simplicity */}
                    {day.events.slice(0, 2).map((event, eventIndex) => (
                      <motion.div
                        key={eventIndex}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: eventIndex * 0.1 }}
                        className="flex items-start space-x-4"
                      >
                        <div className="flex-shrink-0">
                          <ClockIcon className="w-6 h-6 text-golf-orange" />
                        </div>
                        <div>
                          <p className="text-base sm:text-lg font-semibold text-golf-gray">
                            {event.title}
                          </p>
                          <p className="text-golf-orange">
                            {event.time}
                          </p>
                          <div className="flex items-center mt-1 text-gray-600">
                            <MapPinIcon className="w-4 h-4 mr-1" />
                            <span>{event.location}</span>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                    
                    {day.events.length > 2 && (
                      <div className="text-center text-golf-orange text-sm">
                        + {day.events.length - 2} more events
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">No schedule available yet. Check back soon for updates.</p>
            </div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mt-16"
          >
            <Link
              to="/schedule"
              className="modern-button inline-block px-8 py-4 text-lg text-white font-medium"
            >
              View Full Schedule
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

export default ScheduleSection;